import { assetUrl } from "../utils/paths";
import { Project } from "../types";

const projects: Project[] = [
  {
    id: "jelabty-ai",
    title: "Jelabty",
    subtitle: "AI Djellaba Try-On Platform",
    description:
      "Building the first AI-powered Moroccan djellaba try-on experience that allows users to visualize traditional clothing on themselves before purchasing.",
    year: "2026",
    timeline: "1 Months",
    services: [
      "Product Strategy",
      "AI Integration",
      "UX Architecture",
      "Full-Stack Development"
    ],
    client: {
      name: "Jelabty",
      role: "AI Fashion Startup",
      avatar: assetUrl("clients/jelabtyClient.jpg"),
      quote:
        "The goal was simple: merge Moroccan tradition with modern AI technology to transform how men choose their djellabas.",
    },
    challenges:
      "The challenge was designing a seamless virtual try-on experience where users could upload their photo and instantly visualize Moroccan djellabas with realistic rendering while preserving identity and body proportions.",
    solutions:
      "Engineered an AI-powered try-on pipeline integrating image processing, prompt optimization, and cloud inference. Designed a minimal dark UI optimized for fast photo upload, real-time generation, and mobile-first conversion flows.",
    results: {
      summary:
        "Delivered an innovative AI fashion platform that bridges traditional Moroccan clothing with cutting-edge technology.",
      metrics: [
        { value: "AI", label: "Virtual Try-On Experience" },
        { value: "1", label: "Photo Upload Process" },
        { value: "Instant", label: "Djellaba Visualization" }
      ],
    },
    liveUrl: "https://jelabty.shop",
    imagePc: assetUrl("projects/JelabtyMac.webp"),
    imageTablet: assetUrl("projects/JelabtyTablet.webp"),
    imageMobile: assetUrl("projects/JelabtyMobile.webp"),
    tags: [
      "Next.js",
      "Prisma",
      "AI Image Generation",
      "Cloudinary",
      "Tailwind CSS"
    ],
  },
  {

    id: "keroumi-fitness",
    title: "Keroumi Fitness",
    subtitle: "Premium Fitness Hub",
    description: "Migrating an offline high-performance coaching brand into a premium digital ecosystem.",
    year: "2025",
    timeline: "3 Months",
    services: ["Web Strategy", "Branding", "UX Architecture"],
    client: {
      name: "Yassine Keroumi",
      role: "Professional Fitness Coach & Founder",
      avatar: assetUrl("clients/KeroumiClient.jpg"),
      quote:
        "Working with Essabir felt personal. The process was smooth, the design was stunning, and everything had meaning.",
    },
    challenges:
      "The primary hurdle was migrating an offline high-performance coaching brand into a premium digital ecosystem without losing the personal, high-energy touch of the coach.",
    solutions:
      "Architected a 'Mobile-First' dark-mode experience optimized for gym environments. Implemented strategic conversion funnels and optimized asset delivery for sub-second load times.",
    results: {
      summary:
        "Successfully transitioned the brand from fragmented social media bookings to a unified, professional digital home.",
      metrics: [
        { value: "3x", label: "Engagement Surge" },
        { value: "2x", label: "User Retention" },
        { value: "5", label: "Premium Leads/Month" },
      ],
    },
    liveUrl: "https://keroumifitness.ma/",
    imagePc: assetUrl("projects/KeroumiMac.webp"),
    imageTablet: assetUrl("projects/KeroumiTablet.webp"),
    imageMobile: assetUrl("projects/KeroumiMobile.webp"),
    tags: ["React", "Tailwind CSS", "Framer Motion"],
  },
  {
    id: "horse-stable-reservation",
    title: "Horse Stable & Reservation Management",
    subtitle: "Operations Management",
    year: "2024",
    timeline: "3 Months",
    services: ["Full-stack Engineering", "Database Design"],
    client: {
      name: "Yassine Alami",
      role: "Equestrian Center Manager",
      avatar: assetUrl("clients/AlamiClient.jpg"),
      quote:
        "Essabir transformed our chaotic booking system into a seamless digital experience. The efficiency gain was immediate.",
    },
    challenges:
      "Digitizing complex manual scheduling for horse bookings and stable maintenance, requiring real-time data consistency and a robust backend.",
    solutions:
      "Built a full-stack solution using React.js and ASP.NET Core. Implemented a relational database with SQL Server to manage intricate relationships between horses, clients, and staff.",
    results: {
      summary:
        "Reduced scheduling errors and manual paperwork by providing a scalable, high-performance management tool.",
      metrics: [
        { value: "2.5x", label: "More Clicks" },
        { value: "3x", label: "Longer Session Time" },
        { value: "3", label: "New B2B inquiries/Month" },
      ],
    },
    github:
      "https://github.com/ESSABIR404/PLATFORME_DE_GESTION_DES_R-SERVATIONS_DE_CHEVAUX_ET_D_CURIES",
    imagePc: assetUrl("projects/EcurieMac.webp"),
    imageTablet: assetUrl("projects/EcurieTablet.webp"),
    imageMobile: assetUrl("projects/EcurieMobile.webp"),
    tags: ["ASP.NET Core", "SQL Server", "React.js"],
  },
  {
    id: "Hotel",
    title: "Hotel Management Dashboard",
    subtitle: "SaaS Platform",
    year: "2026",
    timeline: "3 Months",
    services: ["Full-stack Engineering", "UI Design"],
    client: {
      name: "Yassine Essabir",
      role: "Lead Product Engineer",
      avatar: assetUrl("clients/YassineClient.jpg"),
      quote:
        "The level of detail in the dashboard analytics exceeded my expectations. It's now the heart of our operations.",
    },
    challenges:
      "Managing high-density administrative data—occupancy, revenue, and logistics—in a way that remains intuitive for staff on the go.",
    solutions:
      "Developed a data-driven dashboard using modular architecture. Integrated real-time SVG charting and a responsive grid system.",
    results: {
      summary:
        "Drastically reduced task completion time for hotel administrators by centralizing all management modules.",
      metrics: [
        { value: "4x", label: "Interaction Rate" },
        { value: "2.5x", label: "Efficiency Gain" },
        { value: "6", label: "System Inquiries/Month" },
      ],
    },
    imagePc: assetUrl("projects/HotelMac.webp"),
    imageTablet: assetUrl("projects/HotelTablet.webp"),
    imageMobile: assetUrl("projects/HotelMobile.webp"),
    tags: ["React.js", "Chart.js", "Tailwind CSS"],
  },
  {
    id: "e-learning-platform",
    title: "E-Learning Platform",
    subtitle: "EdTech Solution",
    year: "2024",
    timeline: "3 Months",
    services: ["Full-stack Development (MERN)"],
    client: {
      name: "Dr. Ahmed Mansouri",
      role: "Academic Program Director",
      avatar: assetUrl("clients/MansouriClient.jpg"),
      quote:
        "Our students find the platform incredibly intuitive. The transition from physical to digital learning was flawless.",
    },
    challenges:
      "Creating an engaging, user-friendly educational environment that manages complex authentication and course progress.",
    solutions:
      "Leveraged the MERN stack to build a fast SPA. Utilized modular SCSS to ensure a consistent and scalable design system.",
    results: {
      summary:
        "Produced a seamless digital learning experience that allows students to navigate courses intuitively.",
      metrics: [
        { value: "3.5x", label: "More Course Clicks" },
        { value: "2x", label: "Higher Retention" },
        { value: "10", label: "Student Inquiries/Month" },
      ],
    },
    github: "https://github.com/ESSABIR404/e-learning",
    imagePc: assetUrl("projects/LearningMac.webp"),
    imageTablet: assetUrl("projects/LearningTablet.webp"),
    imageMobile: assetUrl("projects/LearningMobile.webp"),
    tags: ["MERN Stack", "SCSS", "Node.js"],
  },
  {
    id: "essabir-web-solutions",
    title: "ESSABIR-WEB-SOLUTIONS",
    subtitle: "Agency Portfolio",
    year: "2024",
    timeline: "3 Months",
    services: ["Agency Portfolio", "Next.js Development"],
    client: {
      name: "Essabir Team",
      role: "Digital Solutions Agency",
      avatar: assetUrl("clients/AgencyClient.jpg"),
      quote:
        "This platform perfectly represents our commitment to high-performance and modern aesthetics.",
    },
    challenges:
      "Designing a portfolio site that acts as a 'proof of concept' for technical expertise with high-end animations.",
    solutions:
      "Used Next.js for SSR and Framer Motion for sophisticated UI interactions. Styled with Tailwind CSS.",
    results: {
      summary:
        "Successfully launched a high-performance brand hub that effectively showcases technical services.",
      metrics: [
        { value: "3x", label: "More Portfolio Clicks" },
        { value: "2x", label: "Longer Engagement" },
        { value: "4", label: "New Leads/Month" },
      ],
    },
    liveUrl: "https://essabir-web-solutions.onrender.com",
    imagePc: assetUrl("projects/EWSMac.jpg"),
    imageTablet: assetUrl("projects/EWSTablet.jpg"),
    imageMobile: assetUrl("projects/EWSMobile.jpg"),
    tags: ["Next.js", "Framer Motion", "Tailwind CSS"],
  },
  {
    id: "gestion-de-stock",
    title: "Gestion de Stock (Hanoty)",
    subtitle: "Inventory Management",
    year: "2024",
    timeline: "3 Months",
    services: ["Inventory System (PHP/MySQL)"],
    client: {
      name: "Mohamed Benjelloun",
      role: "Retail Store Owner",
      avatar: assetUrl("clients/BenjellounClient.jpg"),
      quote:
        "Managing stock used to be a nightmare. Hanoty simplified everything, allowing us to focus on our customers.",
    },
    challenges:
      "Building a lightweight, reliable inventory system for small businesses without the overhead of heavy frameworks.",
    solutions:
      "Developed the application using PHP and MySQL, utilizing AJAX/jQuery for 'real-time' data updates.",
    results: {
      summary:
        "Delivered an efficient internal tool that provides accurate tracking of articles and sales.",
      metrics: [
        { value: "2.5x", label: "Faster Search" },
        { value: "2x", label: "Efficiency Gain" },
        { value: "2", label: "B2B Leads/Month" },
      ],
    },
    github: "https://github.com/ESSABIR404/gestion_de_stock_hanoty",
    imagePc: assetUrl("projects/HanotyMac.jpg"),
    imageTablet: assetUrl("projects/HanotyTablet.jpg"),
    imageMobile: assetUrl("projects/HanotyMobile.jpg"),
    tags: ["PHP", "MySQL", "AJAX", "jQuery"],
  },
];

export default projects;
